import React, { useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Icon from '../../icon/Icon'
import Text from '../../Text'
import Button from '../../Button'
import { ContactAvatar } from './ContactAvatar'

// Styles
import { fastTransition } from '../../../styles/effects'

// Utils
import { getContentType } from '../../../utils/onboardingHelper'

// Types
import {
  OnboardingContactType,
  OnboardingContentType,
} from '../../../common/models/OnboardingTemplates'
import { ICompanyMember } from '../../../common/models/Company'
import { IconType } from '../../icon/IconType'

export const SelectContact: React.FC<{
  title: string | React.ReactNode
  contact?: OnboardingContactType
  content?: OnboardingContentType
  contactMember?: ICompanyMember
  subtitle?: string | React.ReactNode
  actionIcon?: IconType
  actionLabel?: string
  selected?: boolean
  disabled?: boolean
  action?: () => void
  actionElement?: React.ReactNode
  className?: string
  // Cypress handle, forwarded to the clickable row.
  'data-cy'?: string
}> = ({
  contact,
  contactMember,
  content,
  action,
  title,
  subtitle,
  actionIcon,
  actionLabel,
  actionElement,
  selected,
  disabled,
  className,
  'data-cy': dataCy,
}) => {
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()

  // Manage hover state
  const [isHovered, setIsHovered] = useState(false)

  return (
    <SelectContainer
      className={className}
      data-cy={dataCy}
      onClick={action}
      selected={selected}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {(contact || contactMember) && (
        <ContactAvatar contactType={contact} member={contactMember} />
      )}
      {content && (
        <StyledIcon
          type={getContentType(content).icon as IconType}
          size={24}
          color={theme.colors.textLight}
        />
      )}
      <div>
        <Text
          textStyle={contactMember || content ? 'labelHeavy' : 'labelItalic'}
        >
          {title}
        </Text>
        {subtitle && (
          <Text textStyle="label" color="textLight" marginTop={4}>
            {subtitle}
          </Text>
        )}
      </div>
      {actionElement || (
        <Cross
          caption={
            disabled
              ? actionLabel
              : isHovered
              ? actionLabel ||
                (actionIcon ? undefined : t('general.actions.select'))
              : selected
              ? actionLabel
              : actionIcon
              ? undefined
              : t('general.actions.select')
          }
          icon={actionIcon}
          color="primaryLight"
          textColor="textLighter"
          hoverColor="lightest"
          hoverTextColor={disabled ? 'textLighter' : 'primary'}
        />
      )}
    </SelectContainer>
  )
}

const Cross = styled(Button)`
  margin-left: auto;
  /* A bare cross, no button chrome: no padding, and no minimum height. */
  min-height: 0;
  padding: 0;
`

const SelectContainer = styled.div<{
  selected?: boolean
  disabled?: boolean
}>`
  border-radius: 4px;
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  background: transparent;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  transition: background ${fastTransition};

  ${({ selected, theme, disabled }) =>
    selected &&
    !disabled &&
    `
    background-color: ${theme.colors.primaryLight} }!important;
    * {
      color: ${theme.colors.primary};
      }
  `}

  ${Cross} {
    cursor: ${({ disabled }) => (disabled ? 'unset' : 'pointer')};
  }

  &:hover {
    background: ${({ theme, disabled }) =>
      !disabled && theme.colors.primaryFadeLight};

    * {
      color: ${({ theme, disabled }) => !disabled && theme.colors.primary};
    }

    ${Cross} {
      background-color: transparent;

      svg > path {
        fill: ${({ theme }) => theme.colors.primary};
      }

      > label {
        cursor: ${({ disabled }) => (disabled ? 'unset' : 'pointer')};
      }
    }
  }
`

const StyledIcon = styled(Icon)`
  margin-right: 16px;
`
